import React, { useState } from 'react';
import { fetchProxiedIcs } from '../lib/api';
import { motion } from 'motion/react';
import { X, Calendar } from 'lucide-react';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useDropzone } from 'react-dropzone';

// Convert a wall-clock time stated in a named IANA timezone (e.g. the
// "5:30 PM in America/Chicago" that a TeamSnap/GameChanger feed gives us)
// into a correct absolute instant. We find the zone's UTC offset at that
// moment via Intl, then subtract it. Falls back to treating the time as UTC
// if the zone name is unknown, so a bad feed can never crash the import.
const zonedWallTimeToISO = (
  y: number, mo: number, d: number, h: number, mi: number, timeZone: string,
): string => {
  const naiveUTC = Date.UTC(y, mo - 1, d, h, mi, 0);
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const map: Record<string, string> = {};
    for (const p of dtf.formatToParts(new Date(naiveUTC))) {
      if (p.type !== 'literal') map[p.type] = p.value;
    }
    const hour = map.hour === '24' ? '00' : map.hour;
    const asSeenUTC = Date.UTC(+map.year, +map.month - 1, +map.day, +hour, +map.minute, +map.second);
    const offset = asSeenUTC - naiveUTC;
    return new Date(naiveUTC - offset).toISOString();
  } catch {
    return new Date(naiveUTC).toISOString();
  }
};

// Parse an ICS DTSTART value into an ISO instant, honoring its timezone.
// Handles: ...Z (UTC), TZID-tagged wall times, all-day dates (YYYYMMDD),
// and untagged "floating" times (assumed to be in the calendar's zone).
const parseIcsDate = (value: string, tzid: string | undefined, calTz: string | undefined): string => {
  const year = +value.substring(0, 4);
  const month = +value.substring(4, 6);
  const day = +value.substring(6, 8);
  const hasTime = value.includes('T');

  // All-day events carry no time. Anchor to noon so the date doesn't roll
  // backward for viewers in a timezone west of the team's.
  const hour = hasTime ? +value.substring(9, 11) : 12;
  const min = hasTime ? +value.substring(11, 13) : 0;

  if (value.endsWith('Z')) {
    return new Date(Date.UTC(year, month - 1, day, hour, min, 0)).toISOString();
  }

  const zone = tzid || calTz;
  if (zone) {
    return zonedWallTimeToISO(year, month, day, hour, min, zone);
  }

  // No zone info anywhere — fall back to the viewer's local time.
  return new Date(year, month - 1, day, hour, min, 0).toISOString();
};

const parseICS = (text: string, appTeamName: string) => {
  const games: any[] = [];
  const vevents = text.split('BEGIN:VEVENT');
  vevents.shift(); // Remove header

  // The feed's default timezone, used for events that don't tag their own.
  const calTz = text.match(/X-WR-TIMEZONE:(.*)/)?.[1]?.trim();

  const rawEvents: { summary: string, dateISO: string }[] = [];

  vevents.forEach(event => {
    const summary = (event.match(/SUMMARY:(.*)/)?.[1] || 'Unknown Game').trim();

    // Skip practices
    if (summary.toLowerCase().includes('practice')) return;

    // DTSTART may carry parameters before the value, e.g.
    //   DTSTART;TZID=America/Chicago:20260604T173000
    //   DTSTART;VALUE=DATE:20260604
    //   DTSTART:20260604T000000Z
    // Capture the parameter chunk and the date/time value separately.
    const dtMatch = event.match(/DTSTART([^:\n]*):\s*(\d{8}(?:T\d{6}Z?)?)/);
    if (!dtMatch) return;

    const tzid = dtMatch[1].match(/TZID=([^:;\n]+)/)?.[1]?.trim();
    const dateISO = parseIcsDate(dtMatch[2], tzid, calTz);

    rawEvents.push({ summary, dateISO });
  });

  // Tally team names to find the user's team name in the ICS
  const teamTally: Record<string, number> = {};
  
  const extractTeams = (summary: string) => {
    // Delimiters: " at ", " @ ", " vs ", " vs. ", " v ", " v. "
    const match = summary.match(/(.*?)(?:\s+at\s+|\s+@\s+|\s+vs\.?\s+|\s+v\.?\s+)(.*)/i);
    if (match) {
      return [match[1].trim(), match[2].trim()];
    }
    return null;
  };

  rawEvents.forEach(e => {
    const teams = extractTeams(e.summary);
    if (teams) {
      teamTally[teams[0]] = (teamTally[teams[0]] || 0) + 1;
      teamTally[teams[1]] = (teamTally[teams[1]] || 0) + 1;
    }
  });

  // Determine the user's ICS team name
  let userIcsTeamName = '';
  const appTeamNameLower = appTeamName.toLowerCase();
  
  for (const team of Object.keys(teamTally)) {
    if (team.toLowerCase().includes(appTeamNameLower) || appTeamNameLower.includes(team.toLowerCase())) {
      userIcsTeamName = team;
      break;
    }
  }

  // Fallback: pick the most frequent team
  if (!userIcsTeamName) {
    let maxCount = 0;
    for (const [team, count] of Object.entries(teamTally)) {
      if (count > maxCount) {
        maxCount = count;
        userIcsTeamName = team;
      }
    }
  }

  rawEvents.forEach(e => {
    let opponent = e.summary;
    let isHome = false;

    const matchAt = e.summary.match(/(.*?)(?:\s+at\s+|\s+@\s+)(.*)/i);
    const matchVs = e.summary.match(/(.*?)(?:\s+vs\.?\s+|\s+v\.?\s+)(.*)/i);

    if (matchAt) {
      const teamA = matchAt[1].trim(); // Away
      const teamB = matchAt[2].trim(); // Home
      if (teamB === userIcsTeamName) {
        isHome = true;
        opponent = teamA;
      } else {
        isHome = false;
        opponent = teamB;
      }
    } else if (matchVs) {
      const teamA = matchVs[1].trim(); // Home
      const teamB = matchVs[2].trim(); // Away
      if (teamA === userIcsTeamName) {
        isHome = true;
        opponent = teamB;
      } else {
        isHome = false;
        opponent = teamA;
      }
    } else {
      opponent = e.summary.replace(/^Game vs\s+/i, '').replace(/^vs\s+/i, '').replace(/^VS\s+/i, '').trim();
    }

    games.push({
      opponent,
      isHome,
      date: e.dateISO,
      status: 'scheduled',
      createdAt: new Date().toISOString()
    });
  });

  return games;
};

// Returns true if the two opponent strings are likely the same team.
// Handles cases like "KC Pelicans" vs "Pelicans" by checking word overlap.
const fuzzyOpponentMatch = (a: string, b: string): boolean => {
  const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
  const na = normalize(a);
  const nb = normalize(b);
  if (na === nb) return true;
  if (na.includes(nb) || nb.includes(na)) return true;
  // Word overlap: any shared word of 3+ chars is enough
  const wordsA = na.split(/\s+/).filter(w => w.length >= 3);
  const wordsB = new Set(nb.split(/\s+/).filter(w => w.length >= 3));
  return wordsA.some(w => wordsB.has(w));
};

const isSameDay = (isoA: string, isoB: string): boolean => {
  return isoA.substring(0, 10) === isoB.substring(0, 10);
};

export const CalendarImporter = ({ teamId, teamName, onClose }: { teamId: string, teamName: string, onClose: () => void }) => {
  const [isImporting, setIsImporting] = useState(false);
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const processICS = async (text: string) => {
    const games = parseICS(text, teamName);
    if (games.length === 0) {
      throw new Error("No games found in this calendar.");
    }

    // Fetch existing games to avoid duplicates
    const existingSnap = await getDocs(collection(db, `teams/${teamId}/games`));
    const existingGames = existingSnap.docs.map(d => d.data() as { opponent: string; date: string });

    let skipped = 0;
    for (const game of games) {
      const alreadyExists = existingGames.some(
        eg => isSameDay(eg.date, game.date) && fuzzyOpponentMatch(eg.opponent, game.opponent)
      );
      if (alreadyExists) {
        skipped++;
        continue;
      }
      await addDoc(collection(db, `teams/${teamId}/games`), game);
    }

    if (skipped > 0 && skipped === games.length) {
      throw new Error("All games in this calendar already exist. Nothing new to import.");
    }
  };

  const handleUrlSync = async () => {
    if (!url) return;
    setIsImporting(true);
    setError(null);
    try {
      const response = await fetchProxiedIcs(url);
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Failed to sync calendar.");
      }
      const text = await response.text();
      await processICS(text);
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsImporting(false);
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setIsImporting(true);
    setError(null);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result as string;
      try {
        await processICS(text);
        onClose();
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsImporting(false);
      }
    };
    reader.readAsText(file);
  };

  // @ts-ignore
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    accept: { 'text/calendar': ['.ics'] },
    multiple: false
  });

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-surface backdrop-blur-xl flex items-center justify-center p-6"
    >
      <div className="bg-canvas w-full max-w-sm rounded-3xl p-8 border border-hairline space-y-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-ink-2">
          <X className="w-5 h-5" />
        </button>

        <div className="text-center space-y-2">
          <div className="w-16 h-16 bg-canvas rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-accent-ink" />
          </div>
          <h3 className="text-xl font-black italic uppercase">Sync Calendar</h3>
          <p className="text-ink-2 text-sm">Sync your GameChanger schedule in one tap.</p>
        </div>

        <div className="bg-raised rounded-2xl p-4 space-y-2">
          <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">How to find your GC calendar link</p>
          <ol className="space-y-1">
            {[
              "Open GameChanger → your team",
              "Tap the Schedule tab",
              'Tap ⋯ (top right) → "Subscribe to Schedule"',
              "Copy the calendar link and paste it below",
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-[10px] font-black text-accent-ink mt-0.5 shrink-0">{i + 1}.</span>
                <span className="text-[10px] text-ink-2">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Calendar URL</label>
            <div className="flex space-x-2">
              <input 
                value={url}
                onChange={e => setUrl(e.target.value)}
                className="flex-1 bg-raised border-ink-mute rounded-xl p-3 text-xs outline-none focus:ring-1 focus:ring-emerald-500"
                placeholder="https://..."
              />
              <button 
                onClick={handleUrlSync}
                disabled={isImporting || !url}
                className="bg-ink text-surface px-4 rounded-xl text-[10px] font-black uppercase italic disabled:opacity-50"
              >
                Sync
              </button>
            </div>
          </div>

          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-hairline"></div></div>
            <div className="relative flex justify-center text-[10px] font-semibold uppercase tracking-widest">
              <span className="bg-canvas px-2 text-ink-2">Or</span>
            </div>
          </div>

          <div 
            {...getRootProps()} 
            className={cn(
              "border-2 border-dashed rounded-2xl p-8 text-center transition-colors cursor-pointer",
              isDragActive ? "border-ink bg-canvas" : "border-hairline hover:border-ink-mute"
            )}
          >
            <input {...getInputProps()} />
            <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
              {isDragActive ? "Drop file here" : "Drag & drop .ics file"}
            </p>
          </div>

          {error && (
            <p className="text-danger text-[10px] font-semibold uppercase tracking-widest text-center">{error}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const cn = (...classes: any[]) => classes.filter(Boolean).join(' ');
